/**
 * Capitulos que quedaron descargados a medias en el servidor.
 *
 * Suwayomi sirve los capitulos descargados desde su disco y no vuelve a
 * mirar la web nunca mas. Si la auto-descarga salta mientras la fuente esta
 * subiendo el capitulo por tandas —LeerCapitulo lo hace con One Piece—, se
 * guarda lo que hubiera en ese momento y el capitulo se queda mutilado para
 * siempre: en la web original se ve entero y en Yomou no.
 *
 * Paso el 2026-08-18 con One Piece 1191, que se quedo en dos paginas de
 * diecisiete. Se detecta comparando con lo que mide un capitulo normal de la
 * serie y se arregla borrando la descarga y volviendo a preguntar a la fuente.
 */

import {
  borrarDescargas,
  descargarEnServidor,
  obtenerPaginas,
  type Capitulo,
} from '../api/consultas'
import { olvidarImagenes } from './imagenes'

/*
 * Hacen falta unos cuantos hermanos con paginas conocidas para fiarse de la
 * mediana: un capitulo sin abrir tiene pageCount -1, asi que en una serie
 * recien anadida casi todos son desconocidos y cualquier cuenta saldria de
 * dos o tres numeros sueltos.
 */
const MINIMO_MUESTRA = 4

/*
 * Por debajo de esta fraccion de lo normal en la serie, el capitulo canta.
 * Se usa la mitad y no algo mas fino porque los capitulos de verdad varian
 * bastante entre si —One Piece va de 15 a 19 paginas— y lo que se busca aqui
 * son truncamientos evidentes, no capitulos cortos.
 */
const FRACCION_SOSPECHA = 0.5

/** Mediana de paginas de la serie, o 0 si no hay muestra suficiente. */
export function paginasTipicas(hermanos: Pick<Capitulo, 'pageCount'>[]): number {
  const conocidas = hermanos
    .map((c) => c.pageCount)
    .filter((n) => n > 0)
    .sort((a, b) => a - b)

  if (conocidas.length < MINIMO_MUESTRA) return 0
  return conocidas[Math.floor(conocidas.length / 2)] ?? 0
}

/**
 * Si este capitulo parece una descarga a medias.
 *
 * Solo tiene sentido preguntarlo de los capitulos descargados: los demas se
 * resuelven contra la fuente cada vez que se abren, asi que siempre traen lo
 * que haya publicado ahora mismo.
 */
export function pareceTruncado(
  capitulo: Pick<Capitulo, 'isDownloaded'>,
  paginas: number,
  hermanos: Pick<Capitulo, 'pageCount'>[],
): boolean {
  if (!capitulo.isDownloaded || paginas <= 0) return false

  const tipicas = paginasTipicas(hermanos)
  if (!tipicas) return false

  return paginas < tipicas * FRACCION_SOSPECHA
}

/**
 * Tira la descarga a medias y vuelve a resolver las paginas contra la fuente.
 *
 * Se pasan las rutas viejas para sacarlas de la cache: lo que se bajo a
 * medias puede no ser lo definitivo —a veces lo que hay a medio subir es un
 * aviso de "proximamente"— y vive en las mismas rutas que las nuevas, asi que
 * sin olvidarlas el lector seguiria pintando las de antes.
 *
 * Al terminar se vuelve a encolar la descarga: el capitulo estaba descargado
 * por algo (la auto-descarga o el propio usuario) y no es cosa de quitarle la
 * copia del servidor por haberlo arreglado. Si eso falla da igual, el
 * capitulo se lee de la fuente como cualquier otro.
 */
export async function repararCapitulo(
  capituloId: number,
  rutasViejas: string[] = [],
): Promise<string[]> {
  await borrarDescargas([capituloId])
  await olvidarImagenes(rutasViejas)

  const paginas = await obtenerPaginas(capituloId)
  void descargarEnServidor([capituloId]).catch(() => {})
  return paginas
}
